import {
  type ReactElement,
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  ComboBox as AriaComboBox,
  type ComboBoxProps as AriaComboBoxProps,
  Button,
  ListBox,
  Popover,
  type ValidationResult,
} from 'react-aria-components';
import cx from 'clsx';
import { IconChevronBottomOutlined } from '@repo/monochrome-icons';
import { useDebounceValue } from 'usehooks-ts';
import {
  Description,
  Error,
  Input,
  Label,
  Option,
  OptionDivider,
  OptionGroup,
} from '../form-elements';
import styles from './combo-box.module.css';

export interface ComboBoxProps<T extends object> extends Omit<AriaComboBoxProps<T>, 'children'> {
  label: string;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  placeholder: string;
  emptyMessage?: string;
  maxLength?: number;
  multiple?: boolean;
  isOptional?: boolean;
  isOptionalText?: string;
  children: ReactNode | ((item: T) => ReactNode);
  fullWidth?: boolean;
  /** Trailing control with chevron. Ignored when `withIcon` is set. */
  withArrowButton?: boolean;
  /** Decorative trailing icon (same slot as the arrow). Not interactive; use the input to open the list. If both this and `withArrowButton` are set, only the icon is shown. */
  withIcon?: ReactNode;
  placement?: 'bottom start' | 'bottom end';
  inputLabelIcon?: ReactNode;
  inputClassName?: string;
  /** When set, fires after `debounceMs` with no further input changes. `onInputChange` still fires on every keystroke. */
  onDebouncedInputChange?: (value: string) => void;
  /** Debounce delay for `onDebouncedInputChange`. Defaults to `500` (same as `SearchFieldWithDebounce` `timeout`). */
  debounceMs?: number;
}

type ComboBoxInnerProps<T extends object> = Omit<
  ComboBoxProps<T>,
  'onDebouncedInputChange' | 'debounceMs'
>;

function ComboBoxInner<T extends object>({
  label,
  className,
  description,
  errorMessage,
  placeholder,
  emptyMessage,
  maxLength,
  multiple,
  isOptional,
  isOptionalText,
  children,
  fullWidth = true,
  withArrowButton,
  withIcon,
  inputLabelIcon,
  placement = 'bottom start',
  inputClassName,
  ...ariaProps
}: ComboBoxInnerProps<T>): ReactElement {
  // react-aria modal escapes the test container, we render it where it can find the modal (i.e. <div> component with 'ember-testing' id)
  const TEST_PORTAL_CONTAINER = document.getElementById('ember-testing') ?? undefined;
  const inputContainerRef = useRef<HTMLSpanElement>(null);
  const [popoverWidth, setPopoverWidth] = useState<number | undefined>(undefined);

  // Measure trigger width for popover, the trigger’s size is no longer automatically tied to the input’s size
  useLayoutEffect(() => {
    if (!inputContainerRef.current) return;
    setPopoverWidth(inputContainerRef.current.offsetWidth);
  }, []);

  const isInvalid = Boolean(errorMessage);

  return (
    <AriaComboBox {...ariaProps} isInvalid={isInvalid}>
      {({ isOpen }) => (
        <>
          <div className={cx(className, styles.container)}>
            {Boolean(label) && (
              <Label>
                {label} {Boolean(isOptional) && isOptionalText}
              </Label>
            )}
            <span className={cx(styles['input-container'], inputClassName)} ref={inputContainerRef}>
              {inputLabelIcon ? (
                <span aria-hidden="true" className={styles['input-label-icon']}>
                  {inputLabelIcon}
                </span>
              ) : null}
              <Input maxLength={maxLength} multiple={multiple} placeholder={placeholder} />
              {withIcon ? (
                <span aria-hidden="true" className={styles['trailing-icon']}>
                  {withIcon}
                </span>
              ) : null}
              {!withIcon && withArrowButton ? (
                <Button>
                  <IconChevronBottomOutlined
                    className={cx(styles['chevron-icon'], isOpen && styles['chevron-icon-open'])}
                  />
                </Button>
              ) : null}
            </span>
            <Error>{errorMessage}</Error>
            {Boolean(description) && <Description>{description}</Description>}
          </div>
          <Popover
            UNSTABLE_portalContainer={TEST_PORTAL_CONTAINER}
            className={cx('body-standard', styles.popover, fullWidth && styles['full-width'])}
            data-combo-box-popover
            offset={8}
            placement={placement}
            style={popoverWidth ? { width: popoverWidth } : undefined}
            triggerRef={inputContainerRef}
          >
            <ListBox
              className={styles['option-list']}
              renderEmptyState={() =>
                emptyMessage ? <div className={styles['no-results']}>{emptyMessage}</div> : null
              }
            >
              {children}
            </ListBox>
          </Popover>
        </>
      )}
    </AriaComboBox>
  );
}

function ComboBoxWithDebounce<T extends object>(props: ComboBoxProps<T>): ReactElement {
  const {
    onDebouncedInputChange,
    debounceMs = 500,
    inputValue,
    defaultInputValue,
    onInputChange,
    ...restForInner
  } = props;

  const isControlled = inputValue !== undefined;
  const [uncontrolledInput, setUncontrolledInput] = useState(() => defaultInputValue ?? '');
  const trackValue = isControlled ? inputValue : uncontrolledInput;
  const [debouncedValue] = useDebounceValue(trackValue, debounceMs);

  /**
   * useRef is used to create a stable reference to the onDebouncedInputChange callback.
   * This is necessary because the callback is passed as a prop to the inner component,
   * and if it were to change on every render, we'd get an infinite loop.
   */
  const onDebouncedInputChangeRef = useRef(onDebouncedInputChange);
  onDebouncedInputChangeRef.current = onDebouncedInputChange;

  useEffect(() => {
    onDebouncedInputChangeRef.current?.(debouncedValue);
  }, [debouncedValue]);

  const mergedOnInputChange = (value: string): void => {
    if (!isControlled) {
      setUncontrolledInput(value);
    }
    onInputChange?.(value);
  };

  const innerProps: ComboBoxInnerProps<T> = {
    ...restForInner,
    inputValue: isControlled ? inputValue : undefined,
    defaultInputValue: isControlled ? undefined : defaultInputValue,
    onInputChange: mergedOnInputChange,
  };

  return <ComboBoxInner {...innerProps} />;
}

function ComboBoxComponent<T extends object>(props: ComboBoxProps<T>): ReactElement {
  const { onDebouncedInputChange, debounceMs: _debounceMs, ...rest } = props;

  if (onDebouncedInputChange) {
    return <ComboBoxWithDebounce {...props} />;
  }

  return <ComboBoxInner {...rest} />;
}

export const ComboBox = Object.assign(ComboBoxComponent, {
  Option,
  Divider: OptionDivider,
  Group: OptionGroup,
});
